import React from 'react';
import { RollData } from '../types';
import { CoinBadges } from './CoinBadges';
import './RollComplete.css';

interface RollCompleteProps {
  roll: RollData;
}

export const RollComplete: React.FC<RollCompleteProps> = ({ roll }) => {
  const totalCoins = roll.coins.length;
  const totalFaceValNumber = roll.coins.reduce((sum, c) => sum + (c.valuation?.faceValue ?? 2.00), 0);
  const faceValue = `€${totalFaceValNumber.toFixed(2)}`;
  const commemoratives = roll.commemorativeCount;
  const standard = totalCoins - commemoratives;
  const newFinds = roll.newFindsCount;

  const uniqueCountries = Array.from(
    new Set(roll.coins.map(c => c.coin.country))
  );

  const rarestCoin = [...roll.coins].sort((a, b) => {
    const mA = a.coin.mintage === 0 ? 1 : a.coin.mintage;
    const mB = b.coin.mintage === 0 ? 1 : b.coin.mintage;
    return mA - mB;
  })[0]?.coin;

  const highlightCoins = roll.coins.filter(
    c =>
      c.coin.type === 'commemorative' ||
      c.isNewFind ||
      c.coin.mintage === 0 ||
      (c.coin.mintage > 0 && c.coin.mintage <= 5_000_000)
  );

  return (
    <div className="roll-complete-card">
      <h2 className="roll-complete-title">Roll Summary</h2>

      <div className="roll-summary-body">
        {/* Left Column: Stats Table */}
        <div className="summary-left-col">
          <table className="roll-stats-table">
            <tbody>
              <tr>
                <td>Coins Inspected:</td>
                <td>{totalCoins} (1 roll)</td>
              </tr>
              <tr>
                <td>Total Face Value:</td>
                <td>{faceValue}</td>
              </tr>
              <tr>
                <td>Commemorative Coins:</td>
                <td>{commemoratives}</td>
              </tr>
              <tr>
                <td>Standard Issues:</td>
                <td>{standard}</td>
              </tr>
              <tr>
                <td>New Album Finds:</td>
                <td
                  className={`stat-new-finds ${
                    newFinds > 0 ? 'highlight-new' : ''
                  }`}
                >
                  {newFinds} {newFinds === 1 ? 'new coin' : 'new coins'}
                </td>
              </tr>
              <tr>
                <td>Unique Countries:</td>
                <td>
                  {uniqueCountries.length} ({uniqueCountries.slice(0, 3).join(', ')}
                  {uniqueCountries.length > 3 ? '...' : ''})
                </td>
              </tr>
              {rarestCoin && (
                <tr className="top-discovery-row">
                  <td>Top Discovery:</td>
                  <td>
                    {rarestCoin.country} {rarestCoin.year}
                    {(() => {
                      const st = rarestCoin.status || rarestCoin.mintageFormatted;
                      if (st === 'CIRC') return ' (CIRC)';
                      if (st === 'Likely NIFC') return ' (Likely NIFC)';
                      if (st === 'NIFC') return ' (NIFC)';
                      return rarestCoin.mintage > 0
                        ? ` (${rarestCoin.mintage.toLocaleString('en-US')})`
                        : ' (Set Only)';
                    })()}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Right Column: Highlights List */}
        <div className="summary-right-col">
          <div className="highlights-section">
            <div className="highlights-title">Roll Discoveries & Highlights</div>
            {highlightCoins.length > 0 ? (
              <div className="highlights-list">
                {highlightCoins.map((item, idx) => (
                  <div key={idx} className="highlight-item">
                    <CoinBadges
                      coin={item.coin}
                      isNewFind={item.isNewFind}
                      inline
                    />
                    <span className="highlight-name">
                      {item.coin.name ||
                        `${item.coin.country} 2€ (${item.coin.year})`}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="highlights-empty">No rare coins or new finds in this roll.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
